import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import { landingFor } from "../../lib/roles.js";

/**
 * ContributeDocuments — a Contributor's document surface (Oct 24).
 *
 * The sibling of ContributePhotos. A contributor uploads a letter, a plan, an
 * assessment; it waits for the coordinator, who processes it and reviews what
 * it says before anything reaches the profile. She cannot process or review —
 * the server keeps those to editors — and she sees only the documents she
 * added, never the rest of what the profile was built from.
 */

// What a contributor may call a document. Mirrors the server's
// CONTRIBUTOR_DOCUMENT_TYPES; the server refuses anything else.
export const CONTRIBUTOR_DOC_TYPES = [
  { value: "family_letter",       label: "A letter about them" },
  { value: "health_plan",         label: "Health plan" },
  { value: "behavioral_plan",     label: "Behavior plan" },
  { value: "clinical_assessment", label: "Assessment or report" },
  { value: "other",               label: "Something else" },
];

const ACCEPT = ".pdf,.docx,.txt";

export default function ContributeDocuments() {
  const { clientId }       = useParams();
  const { apiFetch, user } = useAuth();
  const fileRef            = useRef(null);

  const [clientName, setClientName] = useState("");
  const [docs, setDocs]       = useState(null);
  const [docType, setDocType] = useState(CONTRIBUTOR_DOC_TYPES[0].value);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);

  async function load() {
    try {
      const [c, d] = await Promise.all([
        apiFetch(`/api/clients/${clientId}`),
        apiFetch(`/api/clients/${clientId}/documents`),
      ]);
      if (c.ok) setClientName((await c.json()).display_name || "");
      if (d.ok) setDocs(await d.json());
    } catch (e) {
      if (!e.sessionEnded) setError("We couldn't load this page just now.");
    }
  }

  useEffect(() => { load(); }, [clientId]);

  async function upload(file) {
    if (!file) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("document_type", docType);
      const r = await apiFetch(`/api/clients/${clientId}/documents/additional`, { method: "POST", body });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Upload failed");
      }
      setNotice(`Thank you — “${file.name}” is with the coordinator, who reviews it before anything goes on the profile.`);
      load();
    } catch (e) {
      if (!e.sessionEnded) setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const typeLabel = v => (CONTRIBUTOR_DOC_TYPES.find(t => t.value === v) || {}).label || "Document";

  return (
    <div style={s.page}>
      <div style={s.content}>
        <Link to={landingFor(user)} style={s.back}>← Back</Link>

        <h1 style={s.title}>Add documents about {clientName || "them"}</h1>
        <p style={s.sub}>
          A letter you've written, a plan, an assessment — anything that would
          help someone new understand them. The coordinator reads it and decides
          what goes on the profile.
        </p>

        <div style={s.row}>
          <select aria-label="What kind of document" value={docType}
                  onChange={e => setDocType(e.target.value)} style={s.select} disabled={busy}>
            {CONTRIBUTOR_DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button type="button" style={{ ...s.primaryBtn, ...(busy ? s.disabled : {}) }}
                  onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? "Adding…" : "Choose a file"}
          </button>
          <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: "none" }}
                 data-testid="doc-input"
                 onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        <p style={s.hint}>PDF, Word or text · up to 20 MB</p>

        {notice && <div style={s.notice}>{notice}</div>}
        {error  && <div style={s.error}>{error}</div>}

        <div style={s.sectionLabel}>Documents you've added</div>
        {docs === null && !error && <p style={s.muted}>Loading…</p>}
        {docs?.length === 0 && <p style={s.muted}>None yet.</p>}
        {docs && docs.length > 0 && (
          <div style={s.list}>
            {docs.map(d => (
              <div key={d.id} style={s.docRow}>
                <span style={s.docName} title={d.filename}>{d.filename}</span>
                <span style={s.docMeta}>
                  {typeLabel(d.document_type)} · {d.reviewed ? "Reviewed" : "Waiting for review"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: "100dvh", background: "var(--color-bg-subtle)", padding: "40px 24px 80px" },
  content:     { maxWidth: 720, margin: "0 auto" },
  back:        { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none" },
  title:       { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", letterSpacing: "-0.5px", margin: "16px 0 8px" },
  sub:         { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: "0 0 28px" },
  row:         { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  select:      { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 12px", background: "var(--color-surface)", color: "var(--color-text-primary)", font: "inherit" },
  hint:        { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: "8px 0 0" },
  sectionLabel:{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "28px 0 10px" },
  list:        { display: "flex", flexDirection: "column", gap: 8 },
  docRow:      { display: "flex", gap: 12, alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px" },
  docName:     { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "1 1 200px" },
  docMeta:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  primaryBtn:  { background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "10px 20px", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", font: "inherit" },
  disabled:    { opacity: 0.5, cursor: "not-allowed" },
  notice:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginTop: 16 },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginTop: 16 },
  muted:       { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
};
